import type { MapProperty } from './types';

/**
 * Deal / opportunity flags computed client-side from the loaded property set.
 * "Below market" = the property's $/SF (from its latest sale) sits meaningfully
 * under the median $/SF for its submarket + property type. "Stale" = a high
 * days-on-market. No backend needed — the map already has all the data.
 */
export interface DealFlag {
  belowMarketPct?: number; // e.g. 18 means 18% below the submarket/type median
  stale?: boolean;
}

const PSF_SANITY_MAX = 5000;   // ignore absurd $/SF outliers when building medians
const MIN_SAMPLE = 5;          // need this many sales to trust a median
const BELOW_THRESHOLD = 0.15;  // 15%+ under median = "below market"
const STALE_DAYS = 180;

const median = (arr: number[]): number => {
  const s = [...arr].sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)];
};

/**
 * Median $/SF keyed by `submarket|property_type` (with a `submarket`-only
 * fallback bucket), built from every loaded property that has a price + size.
 */
export function buildMedianPsf(props: MapProperty[]): Map<string, number> {
  const groups = new Map<string, number[]>();
  const push = (k: string, v: number) => {
    const a = groups.get(k);
    if (a) a.push(v); else groups.set(k, [v]);
  };
  for (const p of props) {
    if (p.submarket && p.price && p.building_size) {
      const psf = p.price / p.building_size;
      if (psf > 0 && psf < PSF_SANITY_MAX) {
        if (p.property_type) push(`${p.submarket}|${p.property_type}`, psf);
        push(p.submarket, psf);
      }
    }
  }
  const med = new Map<string, number>();
  for (const [k, arr] of groups) if (arr.length >= MIN_SAMPLE) med.set(k, median(arr));
  return med;
}

/** Deal flag for one property (null if it isn't flagged). */
export function dealFlagFor(p: MapProperty, med: Map<string, number>): DealFlag | null {
  const flag: DealFlag = {};
  if (p.price && p.building_size && p.submarket) {
    const m = (p.property_type && med.get(`${p.submarket}|${p.property_type}`)) ?? med.get(p.submarket);
    if (m) {
      const pct = (p.price / p.building_size - m) / m;
      if (pct <= -BELOW_THRESHOLD) flag.belowMarketPct = Math.round(-pct * 100);
    }
  }
  if (p.days_on_market != null && p.days_on_market >= STALE_DAYS) flag.stale = true;
  return flag.belowMarketPct != null || flag.stale ? flag : null;
}
